/*
 * Beyond the Fridge — personalised weekly planner generator (screens 07–12).
 *
 * Ported from the design's Component logic. Nothing here draws a design: the supplied master
 * SVG is the artwork. The generator finds the two variable lines in it — the name lockup and
 * the line under it — rewrites them in place in the master's own face, weight and colour, and
 * centres them on the board. The board is then shown on the Create Your Own three-layer render:
 * artwork under the blank photographed product plate on multiply (so gloss, shading and edge
 * roll come from the photograph), with the screw caps on top keeping their own colour.
 *
 * Elements:
 *   <btf-planner-board>   one rendered board (examples on the intro, the live preview)
 *   <btf-planner-preview> the controller on the product page: wires the name fields, the
 *                         variant picker (Orientation / Size / Colour / Cap colour), the
 *                         Product view / On the wall tabs and the price read-outs to the board
 */

// Board sizes in cm, [width, height]
const CM = {
  Landscape: { Small: [55, 40], Medium: [75, 55], Large: [100, 75] },
  Portrait: { Small: [40, 55], Medium: [55, 75], Large: [75, 100] },
};

// The board rectangle inside each production plate, as [left%, top%, width%, height%],
// measured off the Create Your Own "Image Import Area" masters.
const PROD = {
  Landscape: {
    ar: '1000 / 730',
    arNum: 1000 / 730,
    Large: [4.3, 2.9, 90.7, 93.2],
    Medium: [15.8, 15.6, 68.3, 68.6],
    Small: [25.0, 25.1, 49.9, 49.6],
  },
  Portrait: {
    ar: '1 / 1',
    arNum: 1,
    Large: [16.0, 4.3, 68.0, 90.6],
    Medium: [25.0, 15.8, 49.7, 68.3],
    Small: [31.6, 21.0, 36.2, 49.9],
  },
};

// The same for the room composites (all square)
const ROOM = {
  Landscape: { Large: [27.0, 9.6, 47.5, 35.6], Medium: [32.5, 18.9, 35.3, 25.7], Small: [36.5, 20.2, 31.2, 22.6] },
  Portrait: { Large: [33.4, 4.7, 35.2, 47.0], Medium: [36.8, 14.3, 25.7, 35.3], Small: [40.5, 16.0, 22.6, 31.2] },
};

const PLATE = { Small: 'sm', Medium: 'md', Large: 'lg' };
const STAGE_MAX = 430;
const NAME_MAX = 24;
const NAME_FILTER = /[^A-Za-z0-9' &\-]/g;

// Width of each character at 100px in the lockup face, so a name can be fitted to the measure
// without a DOM measurement.
const CHAR_W = {
  A: 68, B: 66, C: 68, D: 70, E: 58, F: 56, G: 72, H: 71, I: 29, J: 52, K: 67, L: 55, M: 88, N: 71, O: 73,
  P: 63, Q: 75, R: 67, S: 62, T: 61, U: 69, V: 67, W: 92, X: 66, Y: 62, Z: 60,
  0: 66, 1: 40, 2: 62, 3: 62, 4: 66, 5: 62, 6: 66, 7: 58, 8: 66, 9: 66,
  ' ': 26, '&': 76, '-': 38, "'": 24,
};

function measure(str) {
  let w = 0;
  for (const ch of String(str || '')) w += (CHAR_W[ch.toUpperCase()] || 62) + 4;
  return w;
}

/** The two numbers in an option value such as "40cm (w) x 55cm (h)" or "Small 55cm x 40cm". */
export function dims(value) {
  const m = String(value || '').match(/(\d+(?:\.\d+)?)\s*(?:cm)?[^\d]+?(\d+(?:\.\d+)?)/);
  return m ? [parseFloat(m[1]), parseFloat(m[2])] : null;
}

export function sizeKey(value) {
  const v = String(value || '');
  if (/small/i.test(v)) return 'Small';
  if (/medium/i.test(v)) return 'Medium';
  if (/large/i.test(v)) return 'Large';
  const n = dims(v);
  if (n) {
    const m = Math.max(n[0], n[1]);
    return m <= 60 ? 'Small' : m <= 80 ? 'Medium' : 'Large';
  }
  return 'Large';
}

export function orientKey(value) {
  return /portrait/i.test(String(value || '')) ? 'Portrait' : 'Landscape';
}

/** Store colour names to the plain-colour master colourways. */
export function colourKey(value) {
  const v = String(value || '').toLowerCase();
  if (/grey|gray|storm/.test(v)) return 'stormy-grey';
  if (/rose|pink/.test(v)) return 'vintage-rose';
  if (/teal/.test(v)) return 'teal';
  return v.trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

let assetMap = null;
function assets() {
  if (assetMap) return assetMap;
  const el = document.querySelector('script[data-btf-planner-assets]');
  try {
    assetMap = el ? JSON.parse(el.textContent) : {};
  } catch (e) {
    assetMap = {};
  }
  return assetMap;
}

const masters = new Map();
function loadMaster(url) {
  if (!masters.has(url)) {
    masters.set(
      url,
      fetch(url).then((r) => {
        if (!r.ok) throw new Error('master ' + r.status);
        return r.text();
      })
    );
  }
  return masters.get(url);
}

// The accumulated matrix of a node's ancestors, so a board coordinate can be expressed in the
// node's own space (the masters set the lockup inside Illustrator transform groups).
function chain(node, root) {
  let a = 1,
    d = 1,
    tx = 0,
    ty = 0;
  const groups = [];
  for (let n = node.parentNode; n && n !== root && n.nodeType === 1; n = n.parentNode) groups.unshift(n);
  for (const g of groups) {
    const t = g.getAttribute && g.getAttribute('transform');
    if (!t || t.indexOf('matrix(') !== 0) continue;
    const p = t.slice(7, t.indexOf(')')).split(',').map(Number);
    if (p.length < 6 || !isFinite(p[0])) continue;
    tx += a * p[4];
    ty += d * p[5];
    a *= p[0];
    d *= p[3];
  }
  return { a: a || 1, d: d || 1, tx, ty };
}

const fontSize = (t) => parseFloat(t.style && t.style.fontSize) || 0;

/**
 * Sets the customer's name and second line into a master, keeping the artwork's own face.
 * @param {string} src - the master SVG source
 * @param {string} name
 * @param {string} strap
 * @returns {string} SVG markup
 */
export function setLockup(src, name, strap) {
  let doc;
  try {
    doc = new DOMParser().parseFromString(src, 'image/svg+xml');
  } catch (e) {
    return '';
  }
  const svg = doc.documentElement;
  if (!svg || svg.nodeName === 'parsererror' || svg.getElementsByTagName('parsererror').length) return '';
  const vb = (svg.getAttribute('viewBox') || '').split(/\s+/).map(Number);
  const W = vb[2] || 11930;
  const H = vb[3] || 8977;
  const portrait = H >= W;
  const texts = Array.prototype.slice.call(doc.getElementsByTagName('text'));

  // the name: every node at display size is part of the one lockup
  const big = texts.filter((t) => fontSize(t) >= 500);
  if (big.length) {
    const primary = big.filter((t) => (t.style.fontWeight || '') === '900')[0] || big[0];
    const held = big.indexOf(primary);
    const { a } = chain(primary, svg);
    const c = chain(big[0], svg);
    const nm = String(name || '').trim() || 'YOUR NAME';
    const maxV = W * (portrait ? 0.8 : 0.62);
    const m = measure(nm) || 1;
    const size = Math.min(fontSize(primary), (100 * (maxV / a)) / m);
    primary.textContent = nm;
    primary.setAttribute('x', ((W / 2 - c.tx) / c.a).toFixed(1));
    primary.setAttribute('y', big[0].getAttribute('y'));
    primary.style.fontSize = size.toFixed(1) + 'px';
    primary.setAttribute('text-anchor', 'middle');
    big.forEach((t, i) => {
      if (i !== held && t.parentNode) t.parentNode.removeChild(t);
    });
  }

  // the second line, if the design carries one
  const line = texts.filter(
    (t) => t.parentNode && fontSize(t) > 240 && fontSize(t) < 460 && /this week|plans/i.test(t.textContent || '')
  )[0];
  if (line) {
    const st = String(strap || '').trim();
    if (!st) {
      line.parentNode.removeChild(line);
    } else {
      const ch = chain(line, svg);
      const m = measure(st) || 1;
      const size = Math.min(fontSize(line), (100 * ((W * 0.52) / ch.a)) / (m * 0.8));
      line.textContent = st;
      line.setAttribute('x', ((W / 2 - ch.tx) / ch.a).toFixed(1));
      line.style.fontSize = size.toFixed(1) + 'px';
      line.setAttribute('text-anchor', 'middle');
    }
  }
  return new XMLSerializer().serializeToString(svg);
}

/**
 * One board on the three-layer render.
 * data-master  URL of the design master for this orientation
 * data-orient  Landscape | Portrait      data-size  Small | Medium | Large (or the store's option value)
 * data-view    product | room            data-cap   white | black
 * data-name / data-strap                 data-max   cap the stage width (px); omit to fill the container
 */
class PlannerBoard extends HTMLElement {
  static get observedAttributes() {
    return ['data-master', 'data-orient', 'data-size', 'data-view', 'data-cap', 'data-name', 'data-strap', 'data-max'];
  }

  #raf = 0;
  #built = false;
  #stage;
  #art;
  #area;
  #caps;
  #want = '';

  connectedCallback() {
    this.#build();
    this.#schedule();
  }

  attributeChangedCallback() {
    if (this.#built) this.#schedule();
  }

  #build() {
    if (this.#built) return;
    this.#built = true;
    this.innerHTML =
      '<span class="btf-pb__stage"><span class="btf-pb__art"></span><span class="btf-pb__area"></span><span class="btf-pb__caps"></span></span>';
    this.#stage = this.firstElementChild;
    [this.#art, this.#area, this.#caps] = Array.from(this.#stage.children);
  }

  #schedule() {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.#render();
    });
  }

  #render() {
    const orient = orientKey(this.dataset.orient);
    const size = sizeKey(this.dataset.size);
    const room = this.dataset.view === 'room';
    const cap = /black/i.test(this.dataset.cap || '') ? 'black' : 'white';
    const cm = CM[orient][size];
    const pr = PROD[orient];
    const r = room ? ROOM[orient][size] : pr[size];
    const plate = (orient === 'Landscape' ? 'land' : 'port') + '-' + PLATE[size];
    const map = assets();
    const base = map[(room ? 'room-' : 'product-') + plate + (room ? '-room' : '-area')] || '';
    const caps = map[(room ? 'room-' : 'product-') + plate + '-caps-' + cap] || '';
    const max = parseInt(this.dataset.max || '', 10);
    const width = max ? 'min(100%, ' + Math.round(max * (room ? 1 : pr.arNum)) + 'px)' : '100%';

    this.#stage.style.cssText =
      'display:block;position:relative;width:' + width + ';height:auto;aspect-ratio:' + (room ? '1 / 1' : pr.ar) +
      ';background:#fff;isolation:isolate;margin:0 auto;';
    this.#art.style.cssText =
      'display:block;position:absolute;left:' + r[0] + '%;top:' + r[1] + '%;width:' + r[2] + '%;height:' +
      (r[3] + (room ? 0 : 0.5)) + '%;z-index:1;overflow:hidden;border-radius:' +
      (350 / cm[0]).toFixed(2) + '% / ' + (350 / cm[1]).toFixed(2) + '%;';
    this.#area.style.cssText =
      'display:block;position:absolute;inset:0;z-index:2;pointer-events:none;mix-blend-mode:multiply;background-image:url("' +
      base + '");background-size:100% 100%;background-repeat:no-repeat;';
    this.#caps.style.cssText =
      'display:block;position:absolute;inset:0;z-index:3;pointer-events:none;background-image:url("' + caps +
      '");background-size:100% 100%;background-repeat:no-repeat;';

    const url = this.dataset.master || '';
    const name = this.dataset.name || '';
    const strap = this.dataset.strap || '';
    const want = [url, name, strap].join('\u0001');
    if (want === this.#want) return;
    this.#want = want;
    if (!url) {
      this.#art.innerHTML = '';
      return;
    }
    loadMaster(url)
      .then((src) => {
        if (this.#want !== want) return;
        this.#art.innerHTML = setLockup(src, name, strap);
        this.dataset.ready = 'true';
      })
      .catch(() => {
        if (this.#want === want) this.#art.innerHTML = '';
      });
  }
}

/**
 * The design params a page was opened with (a shared link, assets/btf-share.js), or null when
 * there are none or a basket line is being edited, which btf-edit-line.js restores instead.
 */
function sharedDesign(keys) {
  const p = new URLSearchParams(window.location.search);
  if (p.has('edit') || (window.btfEditLine && window.btfEditLine.active)) return null;
  return keys.some((k) => p.has(k)) ? p : null;
}

/**
 * The product-page controller. Lives in the preview block and reaches the fields and the
 * variant picker through the section, so Horizon's own variant picker, price and buy buttons
 * keep doing their jobs while the board follows along.
 */
class PlannerPreview extends HTMLElement {
  #ready = false;
  #view = 'product';

  connectedCallback() {
    if (this.#ready) return;
    this.#ready = true;
    this.section = this.closest('.shopify-section') || document.body;
    this.board = this.querySelector('btf-planner-board');
    this.masters = this.#json(this.querySelector('script[data-btf-masters]')) || {};
    this.variants = this.#json(this.section.querySelector('script[data-btf-variants]')) || { names: [], list: [] };
    this.picker = this.section.querySelector('variant-picker');
    this.nameInput = this.section.querySelector('[data-btf-name]');
    this.strapInput = this.section.querySelector('[data-btf-strap]');

    this.section.addEventListener('input', (e) => {
      if (e.target === this.nameInput || e.target === this.strapInput) this.#text();
    });
    this.section.addEventListener('change', (e) => {
      const inPicker = this.picker && this.picker.contains(e.target);
      const isCap = e.target instanceof Element && e.target.matches('[data-btf-cap]');
      if (inPicker || isCap) this.#variant();
    });
    // With no size ticked yet, assets/btf-size-first.js keeps a colour change from Horizon, and
    // so from the listener above; it announces the colour with this event instead
    this.section.addEventListener('btf:open-colour', () => this.#variant());
    this.section.addEventListener('click', (e) => {
      const tab = e.target.closest && e.target.closest('[data-btf-view]');
      if (tab && this.section.contains(tab)) {
        e.preventDefault();
        this.#setView(tab.dataset.btfView);
      }
    });
    if (this.nameInput) {
      this.nameInput.addEventListener('focus', () => this.nameInput.select());
    }

    // A shared link (assets/btf-share.js) carries the variant, which Shopify selects, and the
    // words and caps, which go back into the fields here
    const shared = sharedDesign(['name', 'line2', 'caps']);
    if (shared) {
      if (this.nameInput && shared.has('name')) this.nameInput.value = shared.get('name');
      if (this.strapInput && shared.has('line2')) this.strapInput.value = shared.get('line2');
      const cap = this.section.querySelector(`[data-btf-cap][value="${shared.get('caps') === 'black' ? 'Black' : 'White'}"]`);
      if (cap && shared.has('caps')) cap.checked = true;
    }

    this.#text();
    this.#variant();
    this.#setView('product');
    this.#share(Boolean(shared));
  }

  // "Send it to someone before you decide": shown once the design is the customer's own
  #share(open) {
    const share = this.querySelector('btf-share');
    if (!share) return;
    share.provider = {
      params: () => {
        const p = new URLSearchParams();
        if (this.variantId) p.set('variant', String(this.variantId));
        p.set('name', this.nameInput ? this.nameInput.value : '');
        if (this.strapInput && this.strapInput.value) p.set('line2', this.strapInput.value);
        const cap = this.section.querySelector('[data-btf-cap]:checked');
        if (cap) p.set('caps', cap.value.toLowerCase());
        return p;
      },
      tokens: () => ({
        design: this.dataset.design || '',
        name: this.nameInput && this.nameInput.value ? ', “' + this.nameInput.value + '”' : '',
        size: (this.section.querySelector('[data-btf-size-label]') || {}).textContent || '',
        price: (this.section.querySelector('[data-btf-price]') || {}).textContent || '',
      }),
    };
    const show = () => (share.hidden = false);
    if (open) show();
    else {
      this.section.addEventListener('input', (e) => e.isTrusted && show());
      this.section.addEventListener('change', (e) => e.isTrusted && show());
    }
  }

  #json(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  #text() {
    if (this.nameInput) {
      const max = parseInt(this.nameInput.getAttribute('maxlength') || '', 10) || NAME_MAX;
      const clean = this.nameInput.value.toUpperCase().replace(NAME_FILTER, '').slice(0, max);
      if (clean !== this.nameInput.value) this.nameInput.value = clean;
      this.#count('name', clean.length, max);
    }
    if (this.strapInput) {
      const max = parseInt(this.strapInput.getAttribute('maxlength') || '30', 10);
      this.#count('strap', this.strapInput.value.length, max);
    }
    if (this.board) {
      this.board.dataset.name = this.nameInput ? this.nameInput.value : '';
      this.board.dataset.strap = this.strapInput ? this.strapInput.value : '';
    }
  }

  #count(key, n, max) {
    this.section.querySelectorAll('[data-btf-count="' + key + '"]').forEach((el) => {
      el.textContent = n + ' / ' + max;
    });
  }

  #selected() {
    const out = {};
    if (!this.picker) return out;
    this.picker.querySelectorAll('input[type="radio"]:checked').forEach((i) => {
      if (i.dataset.optionName) out[i.dataset.optionName] = i.value;
    });
    this.picker.querySelectorAll('select').forEach((s) => {
      const o = s.selectedOptions[0];
      if (o && o.dataset.optionName) out[o.dataset.optionName] = o.value;
    });
    return out;
  }

  // The picker opened with no size chosen (blocks/btf-product-picker.liquid) and none is ticked
  // yet: the preview stays on the large board and the price reads "From £75"
  #sizeOpen() {
    const g = this.section.querySelector('.btf-picker__group--size');
    return !!(g && g.querySelector('[data-btf-size-prompt]') && !g.querySelector('input:checked'));
  }

  #variant() {
    const sel = this.#selected();
    const names = this.variants.names || [];
    const list = this.variants.list || [];
    const pick = (n) => {
      const k = Object.keys(sel).find((key) => n.test(key));
      return k ? sel[k] : '';
    };
    const orientValue = pick(/orient|format/i);
    const sizeValue = pick(/size/i);
    const colourValue = pick(/colou?r(?!.*cap)/i);
    // Cap colour is a variant option on some products and a line-item property on the rest
    const capRadio = this.section.querySelector('[data-btf-cap]:checked');
    const capValue = capRadio ? capRadio.value : pick(/cap/i);
    const variant = list.find((v) => names.every((n, i) => sel[n] == null || v.options[i] === sel[n])) || list[0];
    this.variantId = variant ? variant.id : null;

    const orient = orientValue ? orientKey(orientValue) : orientKey(this.masters.orient);
    const size = sizeValue ? sizeKey(sizeValue) : 'Large';
    const cm = CM[orient][size];
    const n = dims(sizeValue);
    const wh = n ? (orient === 'Landscape' ? [Math.max(n[0], n[1]), Math.min(n[0], n[1])] : [Math.min(n[0], n[1]), Math.max(n[0], n[1])]) : cm;

    const set = (this.masters.colourways && this.masters.colourways[colourKey(colourValue)]) || this.masters.default || {};
    if (this.board) {
      this.board.dataset.orient = orient;
      this.board.dataset.size = size;
      this.board.dataset.cap = /black/i.test(capValue) ? 'black' : 'white';
      this.board.dataset.master = (orient === 'Portrait' ? set.port : set.land) || '';
    }

    const label = wh[0] + ' × ' + wh[1] + ' cm · ' + orient.toLowerCase();
    this.section.querySelectorAll('[data-btf-size-label]').forEach((el) => (el.textContent = label));
    const open = this.#sizeOpen();
    const price = open ? this.variants.from || '' : variant ? variant.price_formatted : '';
    this.section.querySelectorAll('[data-btf-price]').forEach((el) => (el.textContent = price));
    // "Add to basket — £145": the label carries the price, as drawn
    this.section
      .querySelectorAll('[data-btf-basket-price]')
      .forEach((el) => (el.textContent = !open && variant && variant.available ? ' — ' + price : ''));
  }

  #setView(view) {
    this.#view = view === 'room' ? 'room' : 'product';
    if (this.board) this.board.dataset.view = this.#view;
    this.section.querySelectorAll('[data-btf-view]').forEach((tab) => {
      const on = tab.dataset.btfView === this.#view;
      tab.setAttribute('aria-pressed', on ? 'true' : 'false');
      tab.classList.toggle('is-on', on);
    });
  }
}

if (!customElements.get('btf-planner-board')) customElements.define('btf-planner-board', PlannerBoard);
if (!customElements.get('btf-planner-preview')) customElements.define('btf-planner-preview', PlannerPreview);
